'use client';

import { useState, useEffect, useCallback } from 'react';
import { useRouter } from 'next/navigation';
import Image from 'next/image';
import Link from 'next/link';
import { QuestionnaireData, Question, FormAnswers } from '@/types/questionnaire';

// v0.2.0 section definitions — codes match the corrected migration
// Q-S05 has been replaced by Q-S05a (first source), Q-S05b (did you go elsewhere?),
// Q-S05c (where next? — conditional on Q-S05b = YES)
const SECTIONS = [
  { number: 1, title: 'About the Organisation', layer: 'market_segment', codes: ['Q-C01', 'Q-C02', 'Q-C03', 'Q-C04', 'Q-C05'] },
  { number: 2, title: 'About You', layer: 'respondent', codes: ['Q-R01', 'Q-R02', 'Q-R03'] },
  { number: 3, title: 'The Event', layer: 'journey', codes: ['Q-S01', 'Q-S02', 'Q-S03', 'Q-S04', 'Q-S05a', 'Q-S05b', 'Q-S05c', 'Q-S06', 'Q-S07', 'Q-S08'] },
  { number: 4, title: 'What Happened', layer: 'problem', codes: ['Q-S09', 'Q-S10', 'Q-P01', 'Q-P02'] },
  { number: 5, title: 'Consequences', layer: 'workaround_impact', codes: ['Q-P03', 'Q-I01', 'Q-I02', 'Q-I03'] },
  { number: 6, title: 'Optional Follow-up', layer: 'follow_up', codes: ['Q-F01', 'Q-F02', 'Q-F01-email'] },
];

// Role-adaptive framing for Section 3 intro text.
// Each entry maps a Q-R01 option_code to the appropriate event prompt.
// The underlying evidence constructs remain identical across all roles.
const ROLE_ADAPTIVE_FRAMING: Record<string, string> = {
  PRODUCTION:
    'Think about the most recent time something unexpected happened during production and you needed information quickly to decide what to do next.',
  MAINTENANCE:
    'Think about the most recent breakdown or maintenance event where you needed technical information quickly.',
  ENGINEERING:
    'Think about the most recent process deviation, equipment incident or investigation where you needed historical information or documentation quickly.',
  QUALITY:
    'Think about the most recent quality event where you needed production or material information quickly.',
  WAREHOUSE:
    'Think about the most recent stock, delivery or inventory situation where you needed information quickly.',
  LOGISTICS:
    'Think about the most recent delivery, supplier or transport problem where you needed accurate information quickly.',
  PROCUREMENT:
    'Think about the most recent urgent purchase request, material shortage or supplier problem where you needed information quickly.',
  SALES:
    'Think about the most recent customer issue, late delivery or urgent order where you needed information from operations quickly.',
  FINANCE:
    'Think about the most recent situation where you needed operational or production information quickly to make a financial decision or report.',
  PLANNING:
    'Think about the most recent time a plan changed unexpectedly and you needed accurate information quickly to reschedule or respond.',
  HSE:
    'Think about the most recent safety incident, near-miss or compliance situation where you needed historical information or documentation quickly.',
  MANAGEMENT:
    'Think about the most recent operational problem where you needed accurate information quickly to make a decision or take action.',
};

const DEFAULT_FRAMING =
  'Think about the most recent time something important did not go to plan in your area of work and you needed information quickly to decide what to do next.';

function getRoleAdaptiveFraming(department: string | null | undefined): string {
  if (!department || typeof department !== 'string') return DEFAULT_FRAMING;
  return ROLE_ADAPTIVE_FRAMING[department] || DEFAULT_FRAMING;
}

// Q-S04 role-relevant information type groups.
// The full taxonomy is always available; this controls which group is shown first.
// Cross-functional types remain selectable — this is required for H3 evidence.
const INFO_TYPE_ROLE_GROUPS: Record<string, string[]> = {
  PRODUCTION:   ['INFO_PROD_STATUS', 'INFO_SCHEDULE', 'INFO_MACHINE_STATUS', 'INFO_MATERIAL_AVAIL', 'INFO_WORK_INSTR'],
  MAINTENANCE:  ['INFO_EQUIP_MANUAL', 'INFO_MAINT_HISTORY', 'INFO_SPARE_PARTS', 'INFO_FAULT_INFO', 'INFO_TECH_DRAWING'],
  ENGINEERING:  ['INFO_PROCESS_PARAMS', 'INFO_HISTORIAN', 'INFO_INCIDENT_RECORD', 'INFO_WORK_INSTR', 'INFO_TECH_DRAWING'],
  QUALITY:      ['INFO_BATCH_RECORD', 'INFO_TRACEABILITY', 'INFO_INSPECTION', 'INFO_SUPPLIER_DOC', 'INFO_MATERIAL_SPEC'],
  WAREHOUSE:    ['INFO_STOCK_LEVEL', 'INFO_BIN_LOCATION', 'INFO_PO_STATUS', 'INFO_DELIVERY_DOC', 'INFO_MATERIAL_AVAIL'],
  LOGISTICS:    ['INFO_SHIPMENT_STATUS', 'INFO_ETA', 'INFO_DELIVERY_CONFIRM', 'INFO_PO_STATUS', 'INFO_DISPATCH_DATE'],
  PROCUREMENT:  ['INFO_APPROVED_SUPPLIER', 'INFO_MATERIAL_SPEC', 'INFO_LEAD_TIME', 'INFO_PO_STATUS', 'INFO_STOCK_LEVEL'],
  SALES:        ['INFO_PROD_STATUS', 'INFO_DISPATCH_DATE', 'INFO_STOCK_AVAIL', 'INFO_DELAY_REASON', 'INFO_ETA'],
  FINANCE:      ['INFO_PROD_STATUS', 'INFO_SCHEDULE', 'INFO_MATERIAL_AVAIL', 'INFO_DELIVERY_DOC', 'INFO_PO_STATUS'],
  PLANNING:     ['INFO_SCHEDULE', 'INFO_PROD_STATUS', 'INFO_MATERIAL_AVAIL', 'INFO_MACHINE_STATUS', 'INFO_LEAD_TIME'],
  HSE:          ['INFO_INCIDENT_RECORD', 'INFO_PROCESS_PARAMS', 'INFO_HISTORIAN', 'INFO_WORK_INSTR', 'INFO_BATCH_RECORD'],
  MANAGEMENT:   ['INFO_PROD_STATUS', 'INFO_SCHEDULE', 'INFO_MATERIAL_AVAIL', 'INFO_DELIVERY_DOC', 'INFO_DELAY_REASON'],
};

// Re-order Q-S04 options so role-relevant ones appear first.
// All options remain available — cross-functional types are not hidden.
function reorderInfoTypeOptions(
  options: Question['options'],
  department: string | null | undefined
): Question['options'] {
  if (!options || !department) return options;
  const roleGroup = INFO_TYPE_ROLE_GROUPS[department] || [];
  if (roleGroup.length === 0) return options;

  const primary = options.filter((o) => roleGroup.includes(o.option_code));
  const secondary = options.filter((o) => !roleGroup.includes(o.option_code));
  return [...primary, ...secondary];
}

function getSessionToken(): string {
  if (typeof window === 'undefined') return '';
  let token = sessionStorage.getItem('g5_session_token');
  if (!token) {
    token = Math.random().toString(36).substring(2) + Date.now().toString(36);
    sessionStorage.setItem('g5_session_token', token);
  }
  return token;
}

interface FieldProps {
  question: Question;
  value: string | string[] | number | boolean | null;
  onChange: (code: string, value: string | string[] | number | boolean | null) => void;
  error?: string;
  otherValues: Record<string, string>;
  onOtherChange: (code: string, value: string) => void;
  // For Q-S04: pass reordered options
  overrideOptions?: Question['options'];
}

function QuestionField({ question, value, onChange, error, otherValues, onOtherChange, overrideOptions }: FieldProps) {
  const { question_code: code, question_text, response_type, is_required, help_text } = question;
  const options = overrideOptions ?? question.options;

  const labelClass = 'block text-base font-semibold text-[#1B2A4A] mb-1 leading-snug';
  const inputClass = 'w-full border border-gray-300 rounded-lg px-4 py-3 text-base text-gray-800 focus:outline-none focus:ring-2 focus:ring-[#1B2A4A] focus:border-transparent';
  const errorClass = 'mt-1 text-sm text-red-600';

  return (
    <div className="mb-6">
      <label className={labelClass}>
        {question_text}
        {is_required && <span className="text-[#C9A227] ml-1">*</span>}
      </label>
      {help_text && <p className="text-sm text-gray-500 mb-2">{help_text}</p>}

      {(response_type === 'short_text' || response_type === 'email') && (
        <input
          type={response_type === 'email' ? 'email' : 'text'}
          className={inputClass}
          value={typeof value === 'string' ? value : ''}
          onChange={(e) => onChange(code, e.target.value)}
          placeholder={response_type === 'email' ? 'your@email.com' : 'Your answer'}
        />
      )}

      {response_type === 'long_text' && (
        <textarea
          className={`${inputClass} min-h-[120px] resize-y`}
          value={typeof value === 'string' ? value : ''}
          onChange={(e) => onChange(code, e.target.value)}
          placeholder="Describe in your own words…"
          rows={5}
        />
      )}

      {response_type === 'numeric' && (
        <div className="flex items-center gap-3">
          <input
            type="number"
            min="0"
            className={`${inputClass} max-w-[160px]`}
            value={value !== null && value !== undefined ? String(value) : ''}
            onChange={(e) => onChange(code, e.target.value === '' ? null : Number(e.target.value))}
            placeholder="0"
          />
          <span className="text-gray-500 text-sm">hours</span>
        </div>
      )}

      {response_type === 'scale' && options && (
        <div className="flex flex-wrap gap-3 mt-1">
          {options.map((opt) => (
            <label key={opt.id} className="flex items-center gap-2 cursor-pointer">
              <input
                type="radio"
                name={code}
                value={opt.option_code}
                checked={value === opt.option_code}
                onChange={() => onChange(code, opt.option_code)}
                className="w-5 h-5 accent-[#1B2A4A]"
              />
              <span className="text-sm text-gray-700">{opt.option_label}</span>
            </label>
          ))}
        </div>
      )}

      {(response_type === 'single_choice' || response_type === 'yes_no_unsure') && options && (
        <div className="space-y-2 mt-1">
          {options.map((opt) => (
            <label key={opt.id} className="flex items-center gap-3 cursor-pointer p-3 rounded-lg border border-gray-200 hover:border-[#1B2A4A] hover:bg-blue-50 transition-colors">
              <input
                type="radio"
                name={code}
                value={opt.option_code}
                checked={value === opt.option_code}
                onChange={() => onChange(code, opt.option_code)}
                className="w-5 h-5 accent-[#1B2A4A] flex-shrink-0"
              />
              <span className="text-base text-gray-800">{opt.option_label}</span>
            </label>
          ))}
        </div>
      )}

      {response_type === 'single_choice_other' && options && (
        <div className="space-y-2 mt-1">
          {options.map((opt) => (
            <label key={opt.id} className="flex items-center gap-3 cursor-pointer p-3 rounded-lg border border-gray-200 hover:border-[#1B2A4A] hover:bg-blue-50 transition-colors">
              <input
                type="radio"
                name={code}
                value={opt.option_code}
                checked={value === opt.option_code}
                onChange={() => onChange(code, opt.option_code)}
                className="w-5 h-5 accent-[#1B2A4A] flex-shrink-0"
              />
              <span className="text-base text-gray-800">{opt.option_label}</span>
            </label>
          ))}
          {value === 'OTHER' && (
            <input
              type="text"
              className={`${inputClass} mt-2`}
              placeholder="Please specify…"
              value={otherValues[code] || ''}
              onChange={(e) => onOtherChange(code, e.target.value)}
            />
          )}
        </div>
      )}

      {(response_type === 'multi_choice' || response_type === 'multi_choice_other') && options && (
        <div className="space-y-2 mt-1">
          {options.map((opt) => {
            const selected = Array.isArray(value) ? value.includes(opt.option_code) : false;
            return (
              <label key={opt.id} className="flex items-center gap-3 cursor-pointer p-3 rounded-lg border border-gray-200 hover:border-[#1B2A4A] hover:bg-blue-50 transition-colors">
                <input
                  type="checkbox"
                  value={opt.option_code}
                  checked={selected}
                  onChange={(e) => {
                    const current = Array.isArray(value) ? [...value] : [];
                    if (e.target.checked) {
                      onChange(code, [...current, opt.option_code]);
                    } else {
                      onChange(code, current.filter((v) => v !== opt.option_code));
                    }
                  }}
                  className="w-5 h-5 accent-[#1B2A4A] flex-shrink-0"
                />
                <span className="text-base text-gray-800">{opt.option_label}</span>
              </label>
            );
          })}
          {response_type === 'multi_choice_other' && Array.isArray(value) && value.includes('OTHER') && (
            <input
              type="text"
              className={`${inputClass} mt-2`}
              placeholder="Please specify…"
              value={otherValues[code] || ''}
              onChange={(e) => onOtherChange(code, e.target.value)}
            />
          )}
        </div>
      )}

      {response_type === 'boolean' && (
        <div className="flex gap-4 mt-1">
          {[{ label: 'Yes', val: true }, { label: 'No', val: false }].map(({ label, val }) => (
            <label key={label} className="flex items-center gap-3 cursor-pointer p-3 rounded-lg border border-gray-200 hover:border-[#1B2A4A] hover:bg-blue-50 transition-colors flex-1">
              <input
                type="radio"
                name={code}
                checked={value === val}
                onChange={() => onChange(code, val)}
                className="w-5 h-5 accent-[#1B2A4A] flex-shrink-0"
              />
              <span className="text-base text-gray-800">{label}</span>
            </label>
          ))}
        </div>
      )}

      {error && <p className={errorClass}>{error}</p>}
    </div>
  );
}

export default function ResearchPage() {
  const router = useRouter();
  const [questionnaire, setQuestionnaire] = useState<QuestionnaireData | null>(null);
  const [loading, setLoading] = useState(true);
  const [loadError, setLoadError] = useState<string | null>(null);
  const [currentSection, setCurrentSection] = useState(0);
  const [answers, setAnswers] = useState<FormAnswers>({});
  const [otherValues, setOtherValues] = useState<Record<string, string>>({});
  const [errors, setErrors] = useState<Record<string, string>>({});
  const [submitting, setSubmitting] = useState(false);
  const [startedAt] = useState(() => new Date().toISOString());

  useEffect(() => {
    async function fetchQuestionnaire() {
      try {
        // Explicitly fetch v0.2.0 — does not rely on published-questionnaire RLS
        const res = await fetch('/api/questionnaire');
        if (!res.ok) throw new Error('Failed to load questionnaire');
        const data: QuestionnaireData = await res.json();
        setQuestionnaire(data);
      } catch {
        setLoadError('Unable to load the questionnaire. Please refresh the page.');
      } finally {
        setLoading(false);
      }
    }
    fetchQuestionnaire();
  }, []);

  const handleChange = useCallback((code: string, value: string | string[] | number | boolean | null) => {
    setAnswers((prev) => ({ ...prev, [code]: value }));
    setErrors((prev) => {
      const next = { ...prev };
      delete next[code];
      return next;
    });
  }, []);

  const handleOtherChange = useCallback((code: string, value: string) => {
    setOtherValues((prev) => ({ ...prev, [code]: value }));
  }, []);

  const getSectionQuestions = useCallback((sectionIndex: number): Question[] => {
    if (!questionnaire) return [];
    const section = SECTIONS[sectionIndex];
    return questionnaire.questions.filter((q) => section.codes.includes(q.question_code));
  }, [questionnaire]);

  // Determine if Q-S05c should be shown (conditional on Q-S05b = YES)
  const showQ_S05c = answers['Q-S05b'] === 'YES';

  const validateSection = useCallback((sectionIndex: number): boolean => {
    const questions = getSectionQuestions(sectionIndex);
    const newErrors: Record<string, string> = {};

    for (const q of questions) {
      // Q-S05c is conditional — only validate if Q-S05b = YES
      if (q.question_code === 'Q-S05c') {
        if (answers['Q-S05b'] === 'YES') {
          const val = answers[q.question_code];
          if (!val || val === '') {
            newErrors[q.question_code] = 'Please tell us where you went next.';
          }
        }
        continue;
      }

      if (!q.is_required) continue;
      const val = answers[q.question_code];

      // Email conditional
      if (q.question_code === 'Q-F01-email') {
        const needsEmail = answers['Q-F01'] === true || answers['Q-F02'] === true;
        if (needsEmail && (!val || String(val).trim() === '')) {
          newErrors[q.question_code] = 'Please provide your email address.';
        }
        continue;
      }

      if (val === null || val === undefined || val === '') {
        newErrors[q.question_code] = 'This question is required.';
      } else if (Array.isArray(val) && val.length === 0) {
        newErrors[q.question_code] = 'Please select at least one option.';
      }
    }

    setErrors(newErrors);
    return Object.keys(newErrors).length === 0;
  }, [answers, getSectionQuestions]);

  const handleNext = useCallback(() => {
    if (!validateSection(currentSection)) return;
    setCurrentSection((prev) => Math.min(prev + 1, SECTIONS.length - 1));
    window.scrollTo({ top: 0, behavior: 'smooth' });
  }, [currentSection, validateSection]);

  const handleBack = useCallback(() => {
    setCurrentSection((prev) => Math.max(prev - 1, 0));
    window.scrollTo({ top: 0, behavior: 'smooth' });
  }, []);

  const handleSubmit = useCallback(async () => {
    if (!validateSection(currentSection)) return;
    if (!questionnaire) return;

    setSubmitting(true);
    try {
      const sessionToken = getSessionToken();
      const payload = {
        answers,
        questionnaireVersionId: questionnaire.version.id,
        questions: questionnaire.questions,
        sessionToken,
        metadata: {
          userAgent: navigator.userAgent,
          language: navigator.language,
          startedAt,
        },
      };

      const supabaseUrl = process.env.NEXT_PUBLIC_SUPABASE_URL;
      const anonKey = process.env.NEXT_PUBLIC_SUPABASE_ANON_KEY;
      const edgeFunctionUrl = `${supabaseUrl}/functions/v1/submit-response`;

      const res = await fetch(edgeFunctionUrl, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          'Authorization': `Bearer ${anonKey}`,
          'apikey': anonKey || '',
        },
        body: JSON.stringify(payload),
      });

      if (!res.ok) {
        let errMsg = 'Submission failed';
        try {
          const err = await res.json();
          errMsg = err.error || errMsg;
        } catch {
          // ignore parse error
        }
        throw new Error(errMsg);
      }

      const consentInterview = answers['Q-F01'] === true;
      const consentFindings = answers['Q-F02'] === true;
      const params = new URLSearchParams();
      if (consentInterview) params.set('interview', '1');
      if (consentFindings) params.set('findings', '1');

      router.push(`/complete?${params.toString()}`);
    } catch (err) {
      setErrors({ _submit: err instanceof Error ? err.message : 'Submission failed. Please try again.' });
    } finally {
      setSubmitting(false);
    }
  }, [answers, currentSection, questionnaire, router, startedAt, validateSection]);

  if (loading) {
    return (
      <div className="min-h-screen bg-white flex items-center justify-center">
        <div className="text-center">
          <div className="w-10 h-10 border-4 border-[#1B2A4A] border-t-transparent rounded-full animate-spin mx-auto mb-4" />
          <p className="text-gray-600">Loading questionnaire…</p>
        </div>
      </div>
    );
  }

  if (loadError) {
    return (
      <div className="min-h-screen bg-white flex items-center justify-center px-6">
        <div className="text-center max-w-md">
          <p className="text-red-600 mb-4">{loadError}</p>
          <button
            onClick={() => window.location.reload()}
            className="bg-[#1B2A4A] text-white px-6 py-3 rounded font-semibold hover:bg-[#C9A227] hover:text-[#1B2A4A] transition-colors"
          >
            Retry
          </button>
        </div>
      </div>
    );
  }

  const section = SECTIONS[currentSection];
  const sectionQuestions = getSectionQuestions(currentSection);
  const isLastSection = currentSection === SECTIONS.length - 1;
  const showEmailField = currentSection === 5 && (answers['Q-F01'] === true || answers['Q-F02'] === true);

  // Role-adaptive framing: derive from Q-R01 answer for Section 3
  const departmentAnswer = answers['Q-R01'] as string | null | undefined;
  const roleAdaptiveFraming = getRoleAdaptiveFraming(departmentAnswer);

  return (
    <div className="min-h-screen bg-white font-sans">
      {/* Header */}
      <header className="bg-[#1B2A4A] py-4 px-6">
        <div className="max-w-2xl mx-auto flex items-center justify-between">
          <Link href="/">
            <Image
              src="/assets/images/opex-logo-1789879975019.png"
              alt="OpEx Strategy logo"
              width={130}
              height={40}
              className="h-10 w-auto object-contain"
            />
          </Link>
          <span className="text-blue-200 text-sm">Research Questionnaire</span>
        </div>
      </header>

      {/* Progress bar */}
      <div className="bg-[#F8F9FA] border-b border-gray-200 px-6 py-4">
        <div className="max-w-2xl mx-auto">
          <div className="flex items-center justify-between mb-2">
            <span className="text-sm font-semibold text-[#1B2A4A]">
              Step {currentSection + 1} of {SECTIONS.length}
            </span>
            <span className="text-sm text-gray-500">{section.title}</span>
          </div>
          <div className="w-full bg-gray-200 rounded-full h-2">
            <div
              className="bg-[#1B2A4A] h-2 rounded-full transition-all duration-300"
              style={{ width: `${((currentSection + 1) / SECTIONS.length) * 100}%` }}
            />
          </div>
        </div>
      </div>

      {/* Form */}
      <main className="max-w-2xl mx-auto px-6 py-8">
        <div className="mb-8">
          <h1 className="text-2xl font-bold text-[#1B2A4A] mb-1">
            Section {section.number}: {section.title}
          </h1>

          {/* Section 3: Role-adaptive framing — shown above the first question */}
          {currentSection === 2 && (
            <div className="mt-3 p-4 bg-blue-50 border-l-4 border-[#1B2A4A] rounded-r-lg">
              <p className="text-[#1B2A4A] text-sm font-medium leading-relaxed">
                {roleAdaptiveFraming}
              </p>
              <p className="text-gray-500 text-xs mt-2">
                Please describe a real, recent event — in your own words.
              </p>
            </div>
          )}

          {/* Section 5: Financial impact note */}
          {currentSection === 4 && (
            <p className="mt-2 text-sm text-gray-500">
              All questions in this section are optional. Answer only what you are comfortable sharing.
            </p>
          )}
        </div>

        <div>
          {sectionQuestions.map((question) => {
            // Q-S05c: only render when Q-S05b = YES
            if (question.question_code === 'Q-S05c' && !showQ_S05c) {
              return null;
            }

            // Q-S04: reorder options to show role-relevant ones first
            const overrideOptions =
              question.question_code === 'Q-S04'
                ? reorderInfoTypeOptions(question.options, departmentAnswer)
                : undefined;

            // Q-S04: show a subtle note about role-relevant ordering when department is known
            const showRoleHint =
              question.question_code === 'Q-S04' &&
              departmentAnswer &&
              INFO_TYPE_ROLE_GROUPS[departmentAnswer]?.length > 0;

            return (
              <div key={question.id}>
                {showRoleHint && (
                  <p className="text-xs text-[#1B2A4A] bg-blue-50 border border-blue-100 rounded px-3 py-2 mb-2">
                    The options most relevant to your role are shown first. You can still select any option from the full list — including information from other departments.
                  </p>
                )}
                <QuestionField
                  question={question}
                  value={answers[question.question_code] ?? null}
                  onChange={handleChange}
                  error={errors[question.question_code]}
                  otherValues={otherValues}
                  onOtherChange={handleOtherChange}
                  overrideOptions={overrideOptions}
                />
              </div>
            );
          })}

          {/* Conditional email field in section 6 */}
          {currentSection === 5 && showEmailField && (
            <div className="mb-6">
              <label className="block text-base font-semibold text-[#1B2A4A] mb-1">
                Your email address <span className="text-[#C9A227]">*</span>
              </label>
              <p className="text-sm text-gray-500 mb-2">
                Required for follow-up contact or to receive research findings.
              </p>
              <input
                type="email"
                className="w-full border border-gray-300 rounded-lg px-4 py-3 text-base text-gray-800 focus:outline-none focus:ring-2 focus:ring-[#1B2A4A] focus:border-transparent"
                value={typeof answers['Q-F01-email'] === 'string' ? answers['Q-F01-email'] : ''}
                onChange={(e) => handleChange('Q-F01-email', e.target.value)}
                placeholder="your@email.com"
              />
              {errors['Q-F01-email'] && (
                <p className="mt-1 text-sm text-red-600">{errors['Q-F01-email']}</p>
              )}
            </div>
          )}

          {errors._submit && (
            <div className="mb-4 p-4 bg-red-50 border border-red-200 rounded-lg text-red-700 text-sm">
              {errors._submit}
            </div>
          )}
        </div>

        {/* Navigation */}
        <div className="flex items-center justify-between mt-8 pt-6 border-t border-gray-200">
          <button
            onClick={handleBack}
            disabled={currentSection === 0}
            className="px-6 py-3 border border-gray-300 rounded-lg text-gray-700 font-semibold hover:border-[#1B2A4A] hover:text-[#1B2A4A] transition-colors disabled:opacity-40 disabled:cursor-not-allowed"
          >
            ← Back
          </button>

          {isLastSection ? (
            <button
              onClick={handleSubmit}
              disabled={submitting}
              className="px-8 py-3 bg-[#1B2A4A] text-white font-bold rounded-lg hover:bg-[#C9A227] hover:text-[#1B2A4A] transition-colors disabled:opacity-60 disabled:cursor-not-allowed"
            >
              {submitting ? 'Submitting…' : 'Submit Response'}
            </button>
          ) : (
            <button
              onClick={handleNext}
              className="px-8 py-3 bg-[#1B2A4A] text-white font-bold rounded-lg hover:bg-[#C9A227] hover:text-[#1B2A4A] transition-colors"
            >
              Next →
            </button>
          )}
        </div>
      </main>
    </div>
  );
}
